import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const typing = (el) => !!el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);

// Prev/next for any list viewer: picks the neighbours of `current` in `list` (matched by id).
export const useListNav = (list, current, setCurrent) => {
  const i = current ? list.findIndex((x) => x.id === current.id) : -1;
  return {
    index: i, total: list.length,
    onPrev: i > 0 ? () => setCurrent(list[i - 1]) : null,
    onNext: i >= 0 && i < list.length - 1 ? () => setCurrent(list[i + 1]) : null,
  };
};

// On-screen arrows + ← / → keys for stepping through generated images and videos.
export default function NavArrows({ onPrev, onNext, index = -1, total = 0, portal = false, testid = "nav" }) {
  useEffect(() => {
    const onKey = (e) => {
      if (typing(document.activeElement) || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === "ArrowLeft" && onPrev) { e.preventDefault(); e.stopPropagation(); onPrev(); }
      if (e.key === "ArrowRight" && onNext) { e.preventDefault(); e.stopPropagation(); onNext(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onPrev, onNext]);
  if (!onPrev && !onNext) return null;
  const btn = "absolute top-1/2 -translate-y-1/2 z-[220] h-12 w-12 rounded-full bg-black/60 hover:bg-black/85 border border-white/20 text-white flex items-center justify-center backdrop-blur transition-opacity disabled:opacity-25 disabled:cursor-not-allowed";
  const ui = (
    <div className={`${portal ? "fixed" : "absolute"} inset-0 pointer-events-none z-[215]`} data-testid={`${testid}-arrows`}>
      <button type="button" disabled={!onPrev} onClick={(e) => { e.stopPropagation(); onPrev?.(); }} className={`${btn} left-3 pointer-events-auto`} data-testid={`${testid}-prev`} aria-label="Previous"><ChevronLeft className="w-6 h-6" /></button>
      <button type="button" disabled={!onNext} onClick={(e) => { e.stopPropagation(); onNext?.(); }} className={`${btn} right-3 pointer-events-auto`} data-testid={`${testid}-next`} aria-label="Next"><ChevronRight className="w-6 h-6" /></button>
      {index >= 0 && total > 1 && <span className="absolute top-3 left-1/2 -translate-x-1/2 text-[11px] font-mono text-zinc-300 bg-black/60 rounded-full px-2.5 py-0.5" data-testid={`${testid}-counter`}>{index + 1} / {total} · ← →</span>}
    </div>
  );
  return portal ? createPortal(ui, document.body) : ui;
}
